/**
 * ModelSelector — replaces the native <select> for model + tier selection.
 *
 * Shows a button displaying the currently selected model/tier. Clicking it
 * opens a popover with two sections:
 *   1. Tier defaults ("Choose for me", "Default Fast", "Default Thorough")
 *   2. Individual models — each as a card showing:
 *        • Name
 *        • One-line description
 *        • Capability pills (reasoning, vision, code interpreter)
 *        • Relative cost tier ($, $$, $$$)
 *        • Context window size
 *
 * The value convention mirrors the existing <select> approach:
 *   • "tier:<tier>"  — a tier default (e.g. "tier:fast")
 *   • "<model-id>"   — a concrete model ID
 */

import clsx from "clsx";
import { Brain, ChevronDown, Code, Eye, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { AIModel, ModelCostTier, ModelTierDefault } from "@/types/models";

// ---------------------------------------------------------------------------
// Context window formatter
// ---------------------------------------------------------------------------

function formatContext(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(0)}M ctx`;
  if (tokens >= 1_000) return `${Math.round(tokens / 1_000)}K ctx`;
  return `${tokens} ctx`;
}

// ---------------------------------------------------------------------------
// Cost tier indicator
// ---------------------------------------------------------------------------

function CostTierBadge({ tier }: Readonly<{ tier: ModelCostTier | undefined }>) {
  if (!tier) return null;
  const colors: Record<ModelCostTier, string> = {
    $: "text-emerald-400",
    $$: "text-amber-400",
    $$$: "text-rose-400",
  };
  return (
    <span className={clsx("text-xs font-semibold tabular-nums", colors[tier])} title={`Cost tier: ${tier}`}>
      {tier}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Capability pills
// ---------------------------------------------------------------------------

function CapabilityPills({ model }: Readonly<{ model: AIModel }>) {
  const pills: { label: string; icon: React.ReactNode; show: boolean }[] = [
    { label: "Reasoning", icon: <Brain className="w-3 h-3" />, show: !!model.isReasoning },
    { label: "Vision", icon: <Eye className="w-3 h-3" />, show: !!model.supportsVision },
    { label: "Code", icon: <Code className="w-3 h-3" />, show: !!model.supportsCodeInterpreter },
  ];
  const active = pills.filter((p) => p.show);
  if (active.length === 0) return null;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {active.map((p) => (
        <span
          key={p.label}
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-surface-tertiary text-content-tertiary"
        >
          {p.icon}
          {p.label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ModelCard — one option inside the popover
// ---------------------------------------------------------------------------

function ModelCard({
  model,
  isSelected,
  onSelect,
}: Readonly<{ model: AIModel; isSelected: boolean; onSelect: () => void }>) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={clsx(
        "w-full text-left px-3 py-2.5 rounded-lg transition-colors",
        isSelected
          ? "bg-brand-primary/10 border border-brand-primary/30"
          : "hover:bg-surface-tertiary border border-transparent",
      )}
    >
      {/* Row 1: name + cost + context */}
      <div className="flex items-center gap-2 min-w-0">
        <span className="flex-1 text-sm font-medium text-content-primary truncate">
          {model.displayName ?? model.name}
        </span>
        {model.isDeprecated && (
          <span className="shrink-0 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-amber-500/20 text-amber-800 dark:text-amber-200">
            Deprecated
          </span>
        )}
        <CostTierBadge tier={model.costTier} />
        {model.contextWindow ? (
          <span className="shrink-0 text-[10px] text-content-quaternary tabular-nums">
            {formatContext(model.contextWindow)}
          </span>
        ) : null}
      </div>

      {/* Row 2: description */}
      {model.description && (
        <p className="mt-0.5 text-xs text-content-tertiary line-clamp-2 leading-relaxed">{model.description}</p>
      )}

      {/* Row 3: capability pills */}
      <div className="mt-1">
        <CapabilityPills model={model} />
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// TierCard — a tier default option inside the popover
// ---------------------------------------------------------------------------

function TierCard({
  tierDefault,
  isSelected,
  onSelect,
}: Readonly<{
  tierDefault: ModelTierDefault;
  isSelected: boolean;
  onSelect: () => void;
}>) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={clsx(
        "w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center gap-2",
        isSelected
          ? "bg-brand-primary/10 border border-brand-primary/30"
          : "hover:bg-surface-tertiary border border-transparent",
      )}
    >
      <Zap className="w-3.5 h-3.5 text-content-quaternary shrink-0" />
      <div className="min-w-0">
        <span className="block text-sm font-medium text-content-primary">{tierDefault.label}</span>
        {tierDefault.description && (
          <span className="block text-xs text-content-tertiary truncate">{tierDefault.description}</span>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Resolve display label for the trigger button
// ---------------------------------------------------------------------------

function resolveLabel(value: string, models: AIModel[], tierDefaults: ModelTierDefault[]): string {
  if (value.startsWith("tier:")) {
    const tier = value.slice(5);
    const td = tierDefaults.find((t) => t.tier === tier);
    return td?.label ?? value;
  }
  const model = models.find((m) => m.id === value);
  return model ? (model.displayName ?? model.name) : value;
}

// ---------------------------------------------------------------------------
// ModelSelector — public API
// ---------------------------------------------------------------------------

export interface ModelSelectorProps {
  /** Currently selected value: "tier:<tier>" or a model ID. */
  value: string;
  onChange: (value: string) => void;
  /** Chat-type models to show (already filtered to enabled + chat). */
  models: AIModel[];
  /** User-facing tier defaults to show at the top. */
  tierDefaults: ModelTierDefault[];
  /** When true, the selector renders as disabled. */
  disabled?: boolean;
  /** Additional class names for the trigger button. */
  className?: string;
  /** Accessible label for the trigger button (used when there is no visible label). */
  "aria-label"?: string;
}

export function ModelSelector({
  value,
  onChange,
  models,
  tierDefaults,
  disabled,
  className,
  "aria-label": ariaLabel,
}: Readonly<ModelSelectorProps>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  const label = resolveLabel(value, models, tierDefaults);
  const hasOptions = models.length > 0 || tierDefaults.length > 0;

  function select(v: string) {
    onChange(v);
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={clsx(
          "flex items-center gap-2 pl-4 pr-3 py-2 rounded-lg border transition-colors",
          "bg-surface-secondary text-content-primary border-border-secondary",
          "focus:outline-hidden focus:ring-2 focus:ring-brand-primary",
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-border-primary",
          className,
        )}
      >
        <span className="text-sm font-medium truncate max-w-[180px]">{hasOptions ? label : "No models available"}</span>
        <ChevronDown
          className={clsx("w-4 h-4 text-content-tertiary shrink-0 transition-transform", open && "rotate-180")}
        />
      </button>

      {/* Popover */}
      {open && (
        <div className="absolute z-50 top-full mt-1.5 left-0 w-80 max-h-[480px] overflow-y-auto rounded-xl border border-border-primary bg-surface-primary shadow-xl flex flex-col gap-0.5 p-1.5">
          {/* Tier defaults section */}
          {tierDefaults.length > 0 && (
            <div>
              <p className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-content-quaternary">
                Defaults
              </p>
              <div className="flex flex-col gap-0.5">
                {tierDefaults.map((td) => (
                  <TierCard
                    key={`tier:${td.tier}`}
                    tierDefault={td}
                    isSelected={value === `tier:${td.tier}`}
                    onSelect={() => select(`tier:${td.tier}`)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Models section */}
          {models.length > 0 && (
            <div>
              {tierDefaults.length > 0 && <div className="my-1 border-t border-border-primary" />}
              <p className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-content-quaternary">
                Models
              </p>
              <div className="flex flex-col gap-0.5">
                {models.map((model) => (
                  <ModelCard
                    key={model.id}
                    model={model}
                    isSelected={value === model.id}
                    onSelect={() => select(model.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {!hasOptions && <p className="px-3 py-4 text-center text-sm text-content-tertiary">No models available</p>}
        </div>
      )}
    </div>
  );
}
